import RequestedFeature from '@carvana/merch-contracts/RequestedFeature';
import { useContext, useMemo } from 'react';
import { baseLimitedRequestFeatures } from '~/constants/common/requestedFeatures';
import SortOption from '~/constants/common/SortOption';
import { CONDITIONAL_TILES_SUPPRESSION_EXPERIMENT } from '~/constants/srp/conditionalTilesSuppression';
import AppliedFiltersContext from '~/context/common/AppliedFiltersContext';
import { useIsTradeInApplied } from '~/context/common/TradeContext';
import { useUserLocation } from '~/context/common/UserContext';
import { useDealershipId } from '~/context/dealership/StaticContext';
import { useShowFinance } from '~/context/srp/SearchContext';
import { useVerboseLogging } from '~/context/srp/StaticContext';
import type {
  InventoryRequest,
  InventoryRequestAnalytics
} from '~/contracts/common';
import useIsInExperiment from '~/hooks/common/experiments/useIsInExperiment';
import { isDefined } from '~/utilities/array';
import BrowserCookies from '~/utilities/BrowserCookies';
import resolveConditionalTilesFeature from '~/utilities/srp/resolveConditionalTilesFeature';

const baseRequestedFeatures = [
  RequestedFeature.EarliestAcquisitionBoosting,
  RequestedFeature.ExcludeFacetData,
  RequestedFeature.HideImpossibleCombos,
  RequestedFeature.LoanTermPricing,
  RequestedFeature.LocationBasedPrefiltering,
  RequestedFeature.Personalization
];

const dummyAnalyticsData: InventoryRequestAnalytics = {
  browser: '',
  clientId: '',
  deviceName: '',
  isBot: false,
  isFirstActiveSearchSession: false,
  isMobileDevice: false,
  previousSearchRequestId: '',
  referrer: '',
  searchRequestId: '',
  searchSessionId: '',
  utmParams: {}
};

type Options = {
  includePageNumber: boolean;
  limitRequestedFeatures: boolean;
  shouldGetConditionalTiles: boolean;
};

const defaultOptions: Options = {
  includePageNumber: true,
  limitRequestedFeatures: false,
  shouldGetConditionalTiles: false
};

/**
 * Generates an inventory request object, minus the analytics object.
 *
 * A dummy analytics object will be included, but if you need a complete object
 * then it should be spread into the request object before sending. You can
 * generate the object using the function returned from the
 * `useGenerateAnalyticsData` hook.
 *
 * The complete object is not included because we need it to be memoized, but we
 * also need it to have the latest values from the context. We don't want
 * changes to the analytics object to trigger a new request (thus the
 * memoization), but we do need the latest values to be included in the request.
 * To achieve this, the analytics object has been separated from the request and
 * made into a function that returns the object as needed.
 */
export default function useGenerateInventoryRequest(
  pageSize: number,
  options: Partial<Options> = {}
): {
  request: InventoryRequest;
  conditionalRequestedFeatures: RequestedFeature[];
} {
  const opts: Options = { ...defaultOptions, ...options };

  const { appliedFilters, pageNumber, popstateCount, sortBy } = useContext(
    AppliedFiltersContext
  );
  const dealershipId = useDealershipId();
  const isInConditionalTilesSuppressionTreatment = useIsInExperiment(
    CONDITIONAL_TILES_SUPPRESSION_EXPERIMENT,
    'treatment'
  );
  const showFinance = useShowFinance();
  const { zip = '' } = useUserLocation();
  const shouldApplySprinkles = sortBy === SortOption.MostPopular;
  const verboseLoggingEnabled = useVerboseLogging();

  const requestedFeatures = useMemo<RequestedFeature[]>(
    () =>
      (opts.limitRequestedFeatures
        ? baseLimitedRequestFeatures
        : ([] as Array<RequestedFeature | undefined>)
            .concat(baseRequestedFeatures)
            .concat(
              resolveConditionalTilesFeature(
                dealershipId,
                isInConditionalTilesSuppressionTreatment
              )
            )
            .concat(
              verboseLoggingEnabled
                ? RequestedFeature.ApplyVerboseLogging
                : undefined
            )
            .concat(
              showFinance
                ? undefined
                : RequestedFeature.ExcludeFinanceConditionalTiles
            )
            .concat(
              shouldApplySprinkles ? RequestedFeature.Sprinkles : undefined
            )
      ).filter(isDefined),
    [
      dealershipId,
      isInConditionalTilesSuppressionTreatment,
      opts.limitRequestedFeatures,
      shouldApplySprinkles,
      showFinance,
      verboseLoggingEnabled
    ]
  );

  const isTradeInApplied = useIsTradeInApplied();
  const conditionalRequestedFeatures = useMemo<RequestedFeature[]>(
    () => [
      isTradeInApplied
        ? RequestedFeature.ApplyTradeIn
        : RequestedFeature.FetchTradeIn
    ],
    [isTradeInApplied]
  );

  const page = opts.includePageNumber ? pageNumber : 1;

  const request = useMemo<InventoryRequest>(
    () => ({
      analyticsData: dummyAnalyticsData,
      browserCookieId: BrowserCookies.get('BrowserCookieId') ?? '',
      dealershipId,
      filters: appliedFilters,
      pagination: { page, pageSize },
      requestedFeatures,
      sortBy,
      zip5: zip
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- popstateCount busts the memo on back-nav bail-out
    [
      appliedFilters,
      dealershipId,
      page,
      pageSize,
      popstateCount,
      requestedFeatures,
      sortBy,
      zip
    ]
  );

  return {
    request,
    conditionalRequestedFeatures
  };
}
